import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ReservationFormData } from '../../types';
import {
  validateName,
  validateEmail,
  validatePhone,
  formatPhoneNumber,
} from '../../utils/validation';
import { useBookingStore } from '../../stores/bookingStore';
import { useAuthStore } from '../../stores/authStore';
import LocationSearchModal from '../LocationSearchModal';
import LoginModal from '../auth/LoginModal';
import { productOptionApi, ProductOption } from '../../api/productOptionApi';
import { apiClient } from '../../api/client';
import { localizeLocation, localizeOption } from '../../utils/localization';

interface ReservationFormProps {
  onSubmit: (data: ReservationFormData) => void;
  onChange?: (data: ReservationFormData) => void;
  isLoading?: boolean;
  initialData?: ReservationFormData | null;
}

// 본점 주소
const MAIN_BRANCH_ADDRESS = '부산 부산진구 전포동 674-4';
const MAIN_BRANCH_LAT = 35.15466089304765;
const MAIN_BRANCH_LNG = 129.06050397477015;

// 대여 가능 최소 연령 (대여일 기준 만 나이)
const MIN_RENTAL_AGE = 21;

// yyyy-mm-dd 형식의 생년월일이 실제 존재하는 과거 날짜인지 확인
const isValidBirthDate = (value: string): boolean => {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 8) return false;

  const year = Number(digits.slice(0, 4));
  const month = Number(digits.slice(4, 6));
  const day = Number(digits.slice(6, 8));

  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return false;
  }

  return year >= 1900 && date <= new Date();
};

// 기준일(대여일) 시점의 만 나이 계산
const calculateAgeAt = (birthDate: string, baseDate: Date): number => {
  const digits = birthDate.replace(/\D/g, '');
  const year = Number(digits.slice(0, 4));
  const month = Number(digits.slice(4, 6));
  const day = Number(digits.slice(6, 8));

  let age = baseDate.getFullYear() - year;
  const baseMonth = baseDate.getMonth() + 1;
  if (baseMonth < month || (baseMonth === month && baseDate.getDate() < day)) {
    age -= 1;
  }
  return age;
};

interface FormErrors {
  name?: string;
  birthDate?: string;
  email?: string;
  phone?: string;
  pickupLocation?: string;
  returnLocation?: string;
}

const ReservationForm: React.FC<ReservationFormProps> = ({ onSubmit, onChange, isLoading = false, initialData = null }) => {
  const { t, i18n } = useTranslation();
  const { startDateTime, endDateTime, selectedVehicle, setStartDateTime, setEndDateTime } = useBookingStore();
  const { user, isAuthenticated } = useAuthStore();
  const mainBranchDisplay = t('checkout.mainBranchDisplay');
  const getLocationDisplay = (location?: string) => localizeLocation(location, i18n.language);
  const getOptionDisplay = (option?: string) => localizeOption(option, i18n.language);

  // 배달 불가 날짜 API 조회
  const [deliveryBlockedForPickup, setDeliveryBlockedForPickup] = useState(false);
  const [deliveryBlockedForReturn, setDeliveryBlockedForReturn] = useState(false);
  const [deliveryBlockedName, setDeliveryBlockedName] = useState('');
  const formatDateTime = (date: Date) => date.toLocaleString(i18n.language.startsWith('en') ? 'en-US' : 'ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  useEffect(() => {
    const checkDeliveryBlocked = async () => {
      if (!startDateTime || !endDateTime) {
        setDeliveryBlockedForPickup(false);
        setDeliveryBlockedForReturn(false);
        setDeliveryBlockedName('');
        return;
      }

      try {
        const formatDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const response = await apiClient.get('/delivery-blocked-dates/public/check', {
          params: {
            start_date: formatDate(startDateTime),
            end_date: formatDate(endDateTime),
          },
        });

        const blockedDates = response.data || [];
        if (blockedDates.length > 0) {
          const name = blockedDates[0].name || t('checkout.deliveryBlockedPeriod');
          setDeliveryBlockedName(name);

          // 출차일이 불가 기간에 포함되는지 확인
          const startDateStr = formatDate(startDateTime);
          const pickupBlocked = blockedDates.some((b: any) => startDateStr >= b.start_date && startDateStr <= b.end_date);
          setDeliveryBlockedForPickup(pickupBlocked);

          // 반납일이 불가 기간에 포함되는지 확인
          const endDateStr = formatDate(endDateTime);
          const returnBlocked = blockedDates.some((b: any) => endDateStr >= b.start_date && endDateStr <= b.end_date);
          setDeliveryBlockedForReturn(returnBlocked);
        } else {
          setDeliveryBlockedForPickup(false);
          setDeliveryBlockedForReturn(false);
          setDeliveryBlockedName('');
        }
      } catch {
        setDeliveryBlockedForPickup(false);
        setDeliveryBlockedForReturn(false);
        setDeliveryBlockedName('');
      }
    };

    checkDeliveryBlocked();
  }, [startDateTime, endDateTime]);

  // 픽업(배차) 불가 여부 확인 (24시간 미만 또는 배달 불가 기간)
  const isPickupDisabled = (): boolean => {
    if (!startDateTime || !endDateTime) return false;

    const rentalHours = (endDateTime.getTime() - startDateTime.getTime()) / (1000 * 60 * 60);
    return rentalHours < 24 || deliveryBlockedForPickup;
  };

  // 반납(회차) 불가 여부 확인 (24시간 미만 또는 배달 불가 기간)
  const isReturnDisabled = (): boolean => {
    if (!startDateTime || !endDateTime) return false;

    const rentalHours = (endDateTime.getTime() - startDateTime.getTime()) / (1000 * 60 * 60);
    return rentalHours < 24 || deliveryBlockedForReturn;
  };

  // 픽업 불가 이유 메시지 반환
  const getPickupDisabledMessage = (): string => {
    if (!startDateTime || !endDateTime) return '';

    const rentalHours = (endDateTime.getTime() - startDateTime.getTime()) / (1000 * 60 * 60);
    if (rentalHours < 24) {
      return t('checkout.pickupBranchOnlyUnder24');
    } else if (deliveryBlockedForPickup) {
      return t('checkout.pickupBranchOnlyBlocked', { name: deliveryBlockedName });
    }
    return '';
  };

  // 반납 불가 이유 메시지 반환
  const getReturnDisabledMessage = (): string => {
    if (!startDateTime || !endDateTime) return '';

    const rentalHours = (endDateTime.getTime() - startDateTime.getTime()) / (1000 * 60 * 60);
    if (rentalHours < 24) {
      return t('checkout.returnBranchOnlyUnder24');
    } else if (deliveryBlockedForReturn) {
      return t('checkout.returnBranchOnlyBlocked', { name: deliveryBlockedName });
    }
    return '';
  };

  // 본점 여부 확인 함수
  const isMainBranch = (location: string): boolean => {
    return location.includes(MAIN_BRANCH_ADDRESS) || location.includes('전포동 674-4');
  };

  // 대여/반납 시간 검증 및 조정 함수
  const validateAndAdjustTimes = (
    pickupLoc: string,
    returnLoc: string
  ): { shouldProceed: boolean } => {
    const MIN_RENTAL_HOURS = 5;

    // 대여 시작 시간이 8시, 8시 30분, 21시 30분 또는 22시이고 본점이 아닌 경우
    if (startDateTime && pickupLoc && !isMainBranch(pickupLoc)) {
      const hour = startDateTime.getHours();
      const minute = startDateTime.getMinutes();
      if (hour === 8) {
        const timeStr = minute === 30 ? t('checkout.eightThirty') : t('checkout.eight');
        const timeAdjustment = minute === 30 ? 0.5 : 1; // 8시 30분이면 30분, 8시면 1시간 조정
        const confirmed = window.confirm(
          t('checkout.adjustPickupMorning', { time: timeStr, adjustment: timeAdjustment === 1 ? t('checkout.oneHour') : t('checkout.thirtyMinutes') })
        );
        if (confirmed) {
          const newStartTime = new Date(startDateTime);
          newStartTime.setHours(9, 0, 0, 0);
          setStartDateTime(newStartTime);

          // 반납 시간도 같은 시간만큼 연장하여 최소 5시간 유지
          if (endDateTime) {
            const newEndTime = new Date(endDateTime);
            newEndTime.setTime(newEndTime.getTime() + timeAdjustment * 60 * 60 * 1000);
            setEndDateTime(newEndTime);
          }
        } else {
          return { shouldProceed: false };
        }
      } else if (hour === 21 && minute === 30) {
        const confirmed = window.confirm(
          t('checkout.adjustPickup2130')
        );
        if (confirmed) {
          const newStartTime = new Date(startDateTime);
          newStartTime.setHours(21, 0, 0, 0);
          setStartDateTime(newStartTime);

          // 반납 시간도 30분 단축
          if (endDateTime) {
            const newEndTime = new Date(endDateTime);
            newEndTime.setTime(newEndTime.getTime() - 0.5 * 60 * 60 * 1000);

            // 최소 5시간 체크
            const hoursDiff = (newEndTime.getTime() - newStartTime.getTime()) / (1000 * 60 * 60);
            if (hoursDiff < MIN_RENTAL_HOURS) {
              alert(t('checkout.minimumRentalPickupAlert'));
              return { shouldProceed: false };
            }
            setEndDateTime(newEndTime);
          }
        } else {
          return { shouldProceed: false };
        }
      } else if (hour === 22) {
        const confirmed = window.confirm(
          t('checkout.adjustPickup2200')
        );
        if (confirmed) {
          const newStartTime = new Date(startDateTime);
          newStartTime.setHours(21, 0, 0, 0);
          setStartDateTime(newStartTime);

          // 반납 시간도 1시간 단축
          if (endDateTime) {
            const newEndTime = new Date(endDateTime);
            newEndTime.setTime(newEndTime.getTime() - 1 * 60 * 60 * 1000);

            // 최소 5시간 체크
            const hoursDiff = (newEndTime.getTime() - newStartTime.getTime()) / (1000 * 60 * 60);
            if (hoursDiff < MIN_RENTAL_HOURS) {
              alert(t('checkout.minimumRentalPickupAlert'));
              return { shouldProceed: false };
            }
            setEndDateTime(newEndTime);
          }
        } else {
          return { shouldProceed: false };
        }
      }
    }

    // 반납 시간이 8시, 8시 30분, 21시 30분 또는 22시이고 본점이 아닌 경우
    if (endDateTime && returnLoc && !isMainBranch(returnLoc)) {
      const hour = endDateTime.getHours();
      const minute = endDateTime.getMinutes();
      if (hour === 8) {
        const timeStr = minute === 30 ? t('checkout.eightThirty') : t('checkout.eight');
        const timeAdjustment = minute === 30 ? 0.5 : 1; // 8시 30분이면 30분, 8시면 1시간 조정
        const confirmed = window.confirm(
          t('checkout.adjustReturnMorning', { time: timeStr, adjustment: timeAdjustment === 1 ? t('checkout.oneHour') : t('checkout.thirtyMinutes') })
        );
        if (confirmed) {
          const newEndTime = new Date(endDateTime);
          newEndTime.setHours(9, 0, 0, 0);

          // 대여 시작 시간도 같은 시간만큼 단축
          if (startDateTime) {
            const newStartTime = new Date(startDateTime);
            newStartTime.setTime(newStartTime.getTime() - timeAdjustment * 60 * 60 * 1000);

            // 최소 5시간 체크
            const hoursDiff = (newEndTime.getTime() - newStartTime.getTime()) / (1000 * 60 * 60);
            if (hoursDiff < MIN_RENTAL_HOURS) {
              alert(t('checkout.minimumRentalReturnAlert'));
              return { shouldProceed: false };
            }
            setStartDateTime(newStartTime);
          }
          setEndDateTime(newEndTime);
        } else {
          return { shouldProceed: false };
        }
      } else if (hour === 21 && minute === 30) {
        // 먼저 5시간 미만인지 체크
        let needsStartTimeAdjustment = false;
        if (startDateTime) {
          const tempEndTime = new Date(endDateTime);
          tempEndTime.setHours(21, 0, 0, 0);
          const hoursDiff = (tempEndTime.getTime() - startDateTime.getTime()) / (1000 * 60 * 60);
          needsStartTimeAdjustment = hoursDiff < MIN_RENTAL_HOURS;
        }

        const message = needsStartTimeAdjustment
          ? t('checkout.adjustReturn2130WithStart')
          : t('checkout.adjustReturn2130');

        const confirmed = window.confirm(message);
        if (confirmed) {
          const newEndTime = new Date(endDateTime);
          newEndTime.setHours(21, 0, 0, 0);

          // 최소 5시간 체크 후 필요시 대여 시작 시간 앞당김
          if (startDateTime && needsStartTimeAdjustment) {
            const newStartTime = new Date(newEndTime.getTime() - MIN_RENTAL_HOURS * 60 * 60 * 1000);
            setStartDateTime(newStartTime);
          }
          setEndDateTime(newEndTime);
        } else {
          return { shouldProceed: false };
        }
      } else if (hour === 22) {
        // 먼저 5시간 미만인지 체크
        let needsStartTimeAdjustment = false;
        if (startDateTime) {
          const tempEndTime = new Date(endDateTime);
          tempEndTime.setHours(21, 0, 0, 0);
          const hoursDiff = (tempEndTime.getTime() - startDateTime.getTime()) / (1000 * 60 * 60);
          needsStartTimeAdjustment = hoursDiff < MIN_RENTAL_HOURS;
        }

        const message = needsStartTimeAdjustment
          ? t('checkout.adjustReturn2200WithStart')
          : t('checkout.adjustReturn2200');

        const confirmed = window.confirm(message);
        if (confirmed) {
          const newEndTime = new Date(endDateTime);
          newEndTime.setHours(21, 0, 0, 0);

          // 최소 5시간 체크 후 필요시 대여 시작 시간 앞당김
          if (startDateTime && needsStartTimeAdjustment) {
            const newStartTime = new Date(newEndTime.getTime() - MIN_RENTAL_HOURS * 60 * 60 * 1000);
            setStartDateTime(newStartTime);
          }
          setEndDateTime(newEndTime);
        } else {
          return { shouldProceed: false };
        }
      }
    }

    return { shouldProceed: true };
  };

  const [formData, setFormData] = useState<ReservationFormData>(() => {
    // 픽업/반납 각각 불가 여부 확인
    const rentalHours = startDateTime && endDateTime
      ? (endDateTime.getTime() - startDateTime.getTime()) / (1000 * 60 * 60)
      : 0;
    const isLessThan24Hours = rentalHours < 24;

    const pickupDisabled = startDateTime && endDateTime && isLessThan24Hours;

    const returnDisabled = startDateTime && endDateTime && isLessThan24Hours;

    return initialData || {
      name: '',
      birthDate: '',
      email: '',
      phone: '',
      pickupLocation: pickupDisabled ? MAIN_BRANCH_ADDRESS : '',
      returnLocation: returnDisabled ? MAIN_BRANCH_ADDRESS : '',
      pickupLocationInfo: pickupDisabled ? {
        address: MAIN_BRANCH_ADDRESS,
        lat: MAIN_BRANCH_LAT,
        lng: MAIN_BRANCH_LNG,
      } : undefined,
      returnLocationInfo: returnDisabled ? {
        address: MAIN_BRANCH_ADDRESS,
        lat: MAIN_BRANCH_LAT,
        lng: MAIN_BRANCH_LNG,
      } : undefined,
      selectedInsuranceId: null,
      hasOwnInsurance: '',  // 보험 선택 시 보험 이름으로 설정됨
      specialRequest: t('checkout.none'),
    };
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showValidationError, setShowValidationError] = useState(false);
  const [isPickupModalOpen, setIsPickupModalOpen] = useState(false);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isLoadingUserInfo, setIsLoadingUserInfo] = useState(false);
  const [insuranceOptions, setInsuranceOptions] = useState<ProductOption[]>([]);
  const [selectedInsurance, setSelectedInsurance] = useState<number | null>(initialData?.selectedInsuranceId || null);
  const [equipmentOptions, setEquipmentOptions] = useState<ProductOption[]>([]);
  const [selectedEquipment, setSelectedEquipment] = useState<number[]>(initialData?.selectedEquipmentIds || []);
  const [isLoadingInsurance, setIsLoadingInsurance] = useState(true);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [isAgeRestrictionModalOpen, setIsAgeRestrictionModalOpen] = useState(false);
  const [driverConfirmed, setDriverConfirmed] = useState(false);
  const [driverConfirmError, setDriverConfirmError] = useState(false);

  // 로그인 상태일 때 사용자 정보 자동 입력
  useEffect(() => {
    let isMounted = true;

    const loadUserInfo = async () => {
      // initialData가 이미 있으면 자동 입력하지 않음
      if (initialData && (initialData.name || initialData.email || initialData.phone)) {
        return;
      }

      // 로그인 사용자 정보로 자동 입력
      if (isAuthenticated && user && isMounted) {
        setIsLoadingUserInfo(true);
        try {
          // 백엔드에서 사용자 정보 가져오기 (실명 포함)
          const { getUserByKakaoId } = await import('../../api/userApi');
          const userData = await getUserByKakaoId(String(user.kakaoId));

          const updatedData = {
            ...formData,
            name: userData.name || '',
            email: userData.email || user.email || '',
            phone: userData.phone || user.phone || '',
            birthDate: userData.birth_date || '',
          };
          setFormData(updatedData);

          if (onChange) {
            onChange(updatedData);
          }
        } catch (error) {
          console.error('Failed to load user info:', error);
          // 에러 시 authStore의 정보만 사용
          const updatedData = {
            ...formData,
            email: user.email || '',
            phone: user.phone || '',
          };
          setFormData(updatedData);

          if (onChange) {
            onChange(updatedData);
          }
        } finally {
          setIsLoadingUserInfo(false);
        }
      }
    };

    loadUserInfo();
    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, user, initialData]); // initialData 의존성 추가

  // 보험 옵션 로드 및 필터링
  useEffect(() => {
    let isMounted = true; // Prevent duplicate API calls

    const loadInsuranceOptions = async () => {
      if (!isMounted) return;

      setIsLoadingInsurance(true);
      try {
        const options = await productOptionApi.getAllProductOptions({
          is_active: true,
          type: 'insurance',
        });

        // 같은 sort_order를 가진 보험 그룹으로 묶기
        const groupedBySortOrder = options.reduce((acc, option) => {
          const sortOrder = option.sort_order;
          if (!acc[sortOrder]) {
            acc[sortOrder] = [];
          }
          acc[sortOrder].push(option);
          return acc;
        }, {} as Record<number, ProductOption[]>);

        // 각 그룹에서 우선순위에 따라 하나씩 선택
        const selectedOptions: ProductOption[] = [];
        Object.values(groupedBySortOrder).forEach(group => {
          // 1순위: 해당 차량에 적용되는 보험 (car_names에 차량명이 포함)
          const vehicleSpecific = group.find(option =>
            option.car_names &&
            option.car_names.length > 0 &&
            selectedVehicle &&
            option.car_names.includes(selectedVehicle.model_name)
          );

          if (vehicleSpecific) {
            selectedOptions.push(vehicleSpecific);
            return;
          }

          // 2순위: 전체 차량 적용 보험 (car_names가 null이거나 빈 배열)
          const allVehicles = group.find(option =>
            !option.car_names || option.car_names.length === 0
          );

          if (allVehicles) {
            selectedOptions.push(allVehicles);
          }
        });

        // sort_order 기준으로 정렬
        selectedOptions.sort((a, b) => a.sort_order - b.sort_order);

        if (!isMounted) return;
        setInsuranceOptions(selectedOptions);

        // initialData가 없을 때만 정렬 순서가 가장 낮은 보험을 기본으로 선택
        if (selectedOptions.length > 0 && !initialData?.selectedInsuranceId) {
          const lowestSortOrder = selectedOptions[0]; // 이미 오름차순 정렬되어 있음
          setSelectedInsurance(lowestSortOrder.id);

          // formData 업데이트 및 onChange 호출
          const updatedData = {
            ...formData,
            selectedInsuranceId: lowestSortOrder.id,
            hasOwnInsurance: lowestSortOrder.name  // 선택된 보험 이름 저장
          };
          setFormData(updatedData);
          if (onChange) {
            onChange(updatedData);
          }
        }
      } catch (error) {
        console.error('Failed to load insurance options:', error);
      } finally {
        if (isMounted) {
          setIsLoadingInsurance(false);
        }
      }
    };

    loadInsuranceOptions();
    return () => {
      isMounted = false;
    };
  }, [selectedVehicle]);

  // 장비 옵션 로드
  useEffect(() => {
    let isMounted = true; // Prevent duplicate API calls

    const loadEquipmentOptions = async () => {
      if (!isMounted) return;

      try {
        const options = await productOptionApi.getAllProductOptions({
          is_active: true,
          type: 'equipment',
        });
        // sort_order 기준으로 정렬
        const sortedOptions = options.sort((a, b) => a.sort_order - b.sort_order);

        if (!isMounted) return;
        setEquipmentOptions(sortedOptions);
      } catch (error) {
        console.error('Failed to load equipment options:', error);
      } finally {
        if (isMounted) {
          setIsLoadingOptions(false);
        }
      }
    };

    loadEquipmentOptions();
    return () => {
      isMounted = false;
    };
  }, []);

  const formatBirthDate = (value: string): string => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');

    // Format as yyyy-mm-dd
    if (digits.length <= 4) {
      return digits;
    } else if (digits.length <= 6) {
      return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    } else {
      return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
    }
  };

  // 대여 시작일 기준 만 21세 미만 여부 (대여일이 없으면 오늘 기준)
  const isUnderMinAge = (birthDate: string): boolean => {
    if (!isValidBirthDate(birthDate)) return false;
    return calculateAgeAt(birthDate, startDateTime || new Date()) < MIN_RENTAL_AGE;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    let formattedValue = value;
    if (name === 'phone') {
      formattedValue = formatPhoneNumber(value);
    } else if (name === 'birthDate') {
      formattedValue = formatBirthDate(value);
    }

    const updatedData = { ...formData, [name]: formattedValue };
    setFormData(updatedData);

    // Call onChange callback for real-time updates
    if (onChange) {
      onChange(updatedData);
    }

    // Clear error when user types
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    validateField(name as keyof ReservationFormData);
  };

  const validateField = (fieldName: keyof ReservationFormData): boolean => {
    const value = formData[fieldName];
    let error: string | undefined;

    // LocationInfo 타입 필드는 검증하지 않음
    if (fieldName === 'pickupLocationInfo' || fieldName === 'returnLocationInfo') {
      return true;
    }

    switch (fieldName) {
      case 'name':
        if (!value) {
          error = t('checkout.nameRequired');
        } else if (typeof value === 'string' && !validateName(value)) {
          error = t('checkout.nameInvalid');
        }
        break;

      case 'birthDate':
        if (!value) {
          error = t('checkout.birthDateRequired');
        } else if (typeof value === 'string' && !isValidBirthDate(value)) {
          error = t('checkout.birthDateInvalid');
        } else if (typeof value === 'string' && isUnderMinAge(value)) {
          error = t('checkout.ageRestriction');
        }
        break;


      case 'phone':
        if (!value) {
          error = t('checkout.phoneRequired');
        } else if (typeof value === 'string' && !validatePhone(value)) {
          error = t('checkout.phoneInvalid');
        }
        break;

      case 'pickupLocation':
        // 픽업이 비활성화된 경우(본점 자동 설정) 검증 통과
        if (isPickupDisabled()) {
          break;
        }
        if (!value) {
          error = t('checkout.pickupRequired');
        }
        break;

      case 'returnLocation':
        // 반납이 비활성화된 경우(본점 자동 설정) 검증 통과
        if (isReturnDisabled()) {
          break;
        }
        if (!value) {
          error = t('checkout.returnRequired');
        }
        break;
    }

    setErrors((prev) => ({ ...prev, [fieldName]: error }));
    return !error;
  };

  const validateForm = (): boolean => {
    const fieldsToValidate: (keyof ReservationFormData)[] = [
      'name',
      'birthDate',
      'phone',
      'pickupLocation',
      'returnLocation',
    ];

    const results = fieldsToValidate.map((field) => validateField(field));
    return results.every((result) => result);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched
    setTouched({
      name: true,
      birthDate: true,
      phone: true,
      pickupLocation: true,
      returnLocation: true,
    });

    const isFormValid = validateForm();
    setDriverConfirmError(!driverConfirmed);

    // 대여일 기준 만 21세 미만이면 진행 불가 안내 팝업
    if (isUnderMinAge(formData.birthDate)) {
      setIsAgeRestrictionModalOpen(true);
      return;
    }

    if (isFormValid && driverConfirmed) {
      // Hide validation error if form is valid
      setShowValidationError(false);

      // 제출 전 시간 검증 수행
      const pickupLoc = formData.pickupLocationInfo?.address || formData.pickupLocation || '';
      const returnLoc = formData.returnLocationInfo?.address || formData.returnLocation || '';

      // 대여 시간 검증
      if (startDateTime && pickupLoc && !isMainBranch(pickupLoc)) {
        const hour = startDateTime.getHours();
        const minute = startDateTime.getMinutes();
        if (hour === 8 || (hour === 21 && minute === 30) || hour === 22) {
          alert(t('checkout.pickupTimeRestriction'));
          setIsPickupModalOpen(true);
          return;
        }
      }

      // 반납 시간 검증
      if (endDateTime && returnLoc && !isMainBranch(returnLoc)) {
        const hour = endDateTime.getHours();
        const minute = endDateTime.getMinutes();
        if (hour === 8 || (hour === 21 && minute === 30) || hour === 22) {
          alert(t('checkout.returnTimeRestriction'));
          setIsReturnModalOpen(true);
          return;
        }
      }

      onSubmit(formData);
    } else {
      // Show validation error message
      setShowValidationError(true);
      // Scroll to top to show the error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* 필수정보 입력 안내 메시지 */}
      {showValidationError && (
        <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-red-900 mb-1">
                {t('checkout.requiredInfoTitle')}
              </h3>
              <p className="text-xs text-red-800">
                {t('checkout.requiredInfoDesc')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* 예약정보 섹션 */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-6 pb-4 border-b border-gray-200">{t('checkout.reservationInfo')}</h2>
        <div className="space-y-4">
          {/* 대여일시 (읽기 전용) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('checkout.pickupDateTime')}
            </label>
            <input
              type="text"
              value={startDateTime ? formatDateTime(startDateTime) : ''}
              readOnly
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
            />
          </div>

          {/* 대여위치 */}
          <div>
            <label htmlFor="pickupLocation" className="block text-sm font-medium text-gray-700 mb-1">
              {t('checkout.pickupLocation')} <span className="text-red-500">*</span>
              {isPickupDisabled() && (
                <span className="ml-2 text-xs text-blue-600">({getPickupDisabledMessage()})</span>
              )}
            </label>
            {isPickupDisabled() ? (
              <input
                type="text"
                value={mainBranchDisplay}
                readOnly
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900"
              />
            ) : (
              <>
                <div
                  onClick={() => setIsPickupModalOpen(true)}
                  className={`w-full px-4 py-2 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                    touched.pickupLocation && errors.pickupLocation ? 'border-red-500' : 'border-gray-300'
                  } ${formData.pickupLocation ? 'text-gray-900' : 'text-gray-400'}`}
                >
                  <div className="flex items-center justify-between">
                    <span>{formData.pickupLocation ? getLocationDisplay(formData.pickupLocation) : t('checkout.selectLocationOnMap')}</span>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                </div>
                {touched.pickupLocation && errors.pickupLocation && (
                  <p className="mt-1 text-sm text-red-500">{errors.pickupLocation}</p>
                )}
              </>
            )}
          </div>

          {/* 반납일시 (읽기 전용) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('checkout.returnDateTime')}
            </label>
            <input
              type="text"
              value={endDateTime ? formatDateTime(endDateTime) : ''}
              readOnly
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
            />
          </div>

          {/* 반납위치 */}
          <div>
            <label htmlFor="returnLocation" className="block text-sm font-medium text-gray-700 mb-1">
              {t('checkout.returnLocation')} <span className="text-red-500">*</span>
              {isReturnDisabled() && (
                <span className="ml-2 text-xs text-blue-600">({getReturnDisabledMessage()})</span>
              )}
            </label>
            {isReturnDisabled() ? (
              <input
                type="text"
                value={mainBranchDisplay}
                readOnly
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900"
              />
            ) : (
              <>
                <div
                  onClick={() => setIsReturnModalOpen(true)}
                  className={`w-full px-4 py-2 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                    touched.returnLocation && errors.returnLocation ? 'border-red-500' : 'border-gray-300'
                  } ${formData.returnLocation ? 'text-gray-900' : 'text-gray-400'}`}
                >
                  <div className="flex items-center justify-between">
                    <span>{formData.returnLocation ? getLocationDisplay(formData.returnLocation) : t('checkout.selectLocationOnMap')}</span>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                </div>
                {touched.returnLocation && errors.returnLocation && (
                  <p className="mt-1 text-sm text-red-500">{errors.returnLocation}</p>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* 예약자정보 섹션 */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-6 pb-4 border-b border-gray-200">{t('checkout.customerInfo')}</h2>

        {/* 비로그인 시 카카오 간편가입 유도 배너 */}
        {!isAuthenticated && (
          <div className="mb-6 bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-300 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-bold text-gray-900 mb-1">
                  {t('checkout.kakaoSignupTitle')}
                </h3>
                <p className="text-xs text-gray-600 mb-3">
                  {t('checkout.kakaoSignupDesc')}
                </p>
                <button
                  type="button"
                  onClick={() => setIsLoginModalOpen(true)}
                  className="w-full sm:w-auto bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 3C6.477 3 2 6.477 2 10.5c0 2.184 1.122 4.146 2.904 5.52L3.5 20.5l4.977-2.488C9.459 18.344 10.688 18.5 12 18.5c5.523 0 10-3.477 10-7.5S17.523 3 12 3z" />
                  </svg>
                  {t('checkout.kakaoSignupButton')}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {/* 이름 + 생년월일 */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                {t('checkout.name')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  touched.name && errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={t('checkout.namePlaceholder')}
              />
              {touched.name && errors.name && (
                <p className="mt-1 text-sm text-red-500">{errors.name}</p>
              )}
            </div>

            <div>
              <label htmlFor="birthDate" className="block text-sm font-medium text-gray-700 mb-1">
                {t('checkout.birthDate')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="birthDate"
                name="birthDate"
                value={formData.birthDate}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={10}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  touched.birthDate && errors.birthDate ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="1990-01-01"
              />
              {touched.birthDate && errors.birthDate && (
                <p className="mt-1 text-sm text-red-500">{errors.birthDate}</p>
              )}
            </div>
          </div>

          {/* 전화번호 */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              {t('checkout.phone')} <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                touched.phone && errors.phone ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="010-1234-5678"
            />
            {touched.phone && errors.phone && (
              <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
            )}
          </div>

          {/* 만 21세 이상 + 운전경력 1년 이상 확인 (필수 동의) */}
          <div
            className={`rounded-lg border-2 p-4 transition-colors ${
              driverConfirmError ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-gray-50'
            }`}
          >
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={driverConfirmed}
                onChange={(e) => {
                  setDriverConfirmed(e.target.checked);
                  if (e.target.checked) {
                    setDriverConfirmError(false);
                  }
                }}
                className="mt-0.5 w-5 h-5 flex-shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
              />
              <span className="text-sm text-gray-800">
                <span className="font-bold text-red-500">[{t('checkout.requiredLabel')}]</span>{' '}
                {t('checkout.driverEligibilityConfirm')}
                <span className="block mt-1 text-xs text-gray-500">
                  {t('checkout.driverEligibilityNote')}
                </span>
              </span>
            </label>
            {driverConfirmError && (
              <p className="mt-2 text-sm text-red-500">{t('checkout.driverEligibilityRequired')}</p>
            )}
          </div>
        </div>
      </div>

      {/* 보험정보 섹션 */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold mb-6 pb-4 border-b border-gray-200">{t('checkout.insuranceInfo')}</h2>

        {isLoadingInsurance ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
          </div>
        ) : insuranceOptions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>{t('checkout.noInsuranceOptions')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* 보험 옵션들 */}
            {insuranceOptions.map((option) => (
              <div
                key={option.id}
                onClick={() => {
                  setSelectedInsurance(option.id);
                  const updatedData = {
                    ...formData,
                    selectedInsuranceId: option.id,
                    hasOwnInsurance: option.name  // 선택된 보험 이름 저장
                  };
                  setFormData(updatedData);
                  if (onChange) {
                    onChange(updatedData);
                  }
                }}
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  selectedInsurance === option.id
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedInsurance === option.id ? 'border-blue-600' : 'border-gray-300'
                      }`}>
                        {selectedInsurance === option.id && (
                          <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                        )}
                      </div>
                      <h3 className="font-bold text-base">{getOptionDisplay(option.name)}</h3>
                    </div>
                    {option.description && (
                      <p className="text-sm text-gray-600 mt-2 ml-8">
                        {option.description}
                      </p>
                    )}
                    <div className="ml-8 mt-3 space-y-1">
                      {option.coverage_limit && (
                        <div className="flex items-center gap-2 text-sm">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-gray-700">{t('checkout.coverageLimit')}: {option.coverage_limit}</span>
                        </div>
                      )}
                      {option.customer_burden && (
                        <div className="flex items-center gap-2 text-sm">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-gray-700">{t('checkout.customerBurden')}: {option.customer_burden}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-base text-blue-600">
                      {Math.floor(option.price).toLocaleString()}{t('common.won')}
                      {option.price_type === 'daily' && <span className="text-xs text-gray-500 ml-1">{t('checkout.perDay')}</span>}
                      {option.price_type === 'hourly' && <span className="text-xs text-gray-500 ml-1">{t('checkout.perHour')}</span>}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 기본 종합보험 안내 */}
        <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            {t('checkout.basicInsurance')}
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="text-left py-2 px-3 font-semibold text-gray-700">{t('checkout.category')}</th>
                  <th className="text-left py-2 px-3 font-semibold text-gray-700">{t('checkout.coverage')}</th>
                  <th className="text-left py-2 px-3 font-semibold text-gray-700">{t('checkout.customerBurden')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="py-2 px-3 text-gray-900">{t('checkout.bodilyInjury1')}</td>
                  <td className="py-2 px-3 text-gray-700">{t('checkout.coverage100m')}</td>
                  <td className="py-2 px-3 text-gray-700">{t('checkout.burden500k')}</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 text-gray-900">{t('checkout.bodilyInjury2')}</td>
                  <td className="py-2 px-3 text-gray-700">{t('checkout.unlimited')}</td>
                  <td className="py-2 px-3 text-gray-700">{t('checkout.burden500k')}</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 text-gray-900">{t('checkout.propertyDamage')}</td>
                  <td className="py-2 px-3 text-gray-700">{t('checkout.coverage20m')}</td>
                  <td className="py-2 px-3 text-gray-700">{t('checkout.burden300k')}</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 text-gray-900">{t('checkout.selfInjury')}</td>
                  <td className="py-2 px-3 text-gray-700">{t('checkout.coverage15m')}</td>
                  <td className="py-2 px-3 text-gray-700">{t('checkout.burden500k')}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-gray-600">
            {t('checkout.basicInsuranceNote')}
          </p>
        </div>
      </div>

      {/* 추가옵션 섹션 */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold mb-6 pb-4 border-b border-gray-200">{t('checkout.additionalOptions')}</h2>

        {isLoadingOptions ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600">{t('checkout.loadingOptions')}</p>
          </div>
        ) : equipmentOptions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>{t('checkout.noEquipmentOptions')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* 장비 옵션들 */}
            {equipmentOptions.map((option) => {
              const isSelected = selectedEquipment.includes(option.id);

              return (
                <div
                  key={option.id}
                  onClick={() => {
                    // 같은 sort_order를 가진 다른 옵션 찾기
                    const sameOrderOptions = equipmentOptions
                      .filter(opt => opt.sort_order === option.sort_order)
                      .map(opt => opt.id);

                    let newSelection: number[];
                    if (isSelected) {
                      // 이미 선택된 경우 선택 해제
                      newSelection = selectedEquipment.filter(id => id !== option.id);
                    } else {
                      // 같은 sort_order의 다른 옵션들을 제거하고 현재 옵션 추가
                      newSelection = selectedEquipment.filter(id => !sameOrderOptions.includes(id));
                      newSelection = [...newSelection, option.id];
                    }

                    setSelectedEquipment(newSelection);
                    // 선택된 장비 옵션 이름들을 specialRequest에 저장
                    const selectedNames = equipmentOptions
                      .filter(opt => newSelection.includes(opt.id))
                      .map(opt => opt.name)
                      .join(', ');
                    const updatedData = {
                      ...formData,
                      selectedEquipmentIds: newSelection,
                      specialRequest: selectedNames || t('checkout.none')
                    };
                    setFormData(updatedData);
                    if (onChange) {
                      onChange(updatedData);
                    }
                  }}
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    isSelected
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        isSelected ? 'border-blue-600' : 'border-gray-300'
                      }`}>
                        {isSelected && (
                          <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                        )}
                      </div>
                      <h3 className="font-bold text-base">{getOptionDisplay(option.name)}</h3>
                    </div>
                    {option.description && (
                      <p className="text-sm text-gray-600 mt-2 ml-8">
                        {option.description}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-base text-blue-600">
                      {Math.floor(option.price).toLocaleString()}{t('common.won')}
                      {option.price_type === 'daily' && <span className="text-xs text-gray-500 ml-1">{t('checkout.perDay')}</span>}
                      {option.price_type === 'hourly' && <span className="text-xs text-gray-500 ml-1">{t('checkout.perHour')}</span>}
                    </p>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Submit Button - Desktop */}
      <div className="hidden md:block bg-white rounded-lg shadow-md p-6">
        <button
          type="submit"
          disabled={isLoading}
          className="w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white rounded-lg transition-colors font-semibold"
        >
          {isLoading ? t('common.processing') : t('checkout.nextStep')}
        </button>

        <p className="mt-4 text-sm text-gray-500 text-center">
          {t('checkout.privacyNote')}
        </p>
      </div>

      {/* Submit Button - Mobile Fixed Bottom */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-30">
        <button
          type="submit"
          disabled={isLoading}
          className="w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white rounded-lg transition-colors font-semibold"
        >
          {isLoading ? t('common.processing') : t('checkout.nextStep')}
        </button>
      </div>

      {/* Bottom padding for mobile fixed button */}
      <div className="md:hidden h-20"></div>

      {/* Location Search Modals */}
      <LocationSearchModal
        isOpen={isPickupModalOpen}
        onClose={() => setIsPickupModalOpen(false)}
        onSelectLocation={(address, placeName, lat, lng) => {
          const location = placeName && placeName !== t('checkout.selectedLocation') ? `${placeName} (${address})` : address;

          // 시간 검증 및 조정
          const { shouldProceed } = validateAndAdjustTimes(
            location,
            formData.returnLocation || ''
          );

          // 사용자가 취소를 선택한 경우 false를 반환하여 모달을 열린 상태로 유지
          if (!shouldProceed) {
            return false;
          }

          const updatedData = {
            ...formData,
            pickupLocation: location,
            pickupLocationInfo: { address, lat, lng }
          };
          setFormData(updatedData);
          setTouched({ ...touched, pickupLocation: true });

          // Clear error when location is selected
          if (errors.pickupLocation) {
            setErrors((prev) => ({ ...prev, pickupLocation: undefined }));
          }

          // Hide validation error message when user starts fixing
          if (showValidationError) {
            setShowValidationError(false);
          }

          // Call onChange callback
          if (onChange) {
            onChange(updatedData);
          }

          // true를 반환하여 모달을 닫음
          return true;
        }}
        title={t('checkout.selectPickupLocation')}
      />

      <LocationSearchModal
        isOpen={isReturnModalOpen}
        onClose={() => setIsReturnModalOpen(false)}
        onSelectLocation={(address, placeName, lat, lng) => {
          const location = placeName && placeName !== t('checkout.selectedLocation') ? `${placeName} (${address})` : address;

          // 시간 검증 및 조정
          const { shouldProceed } = validateAndAdjustTimes(
            formData.pickupLocation || '',
            location
          );

          // 사용자가 취소를 선택한 경우 false를 반환하여 모달을 열린 상태로 유지
          if (!shouldProceed) {
            return false;
          }

          const updatedData = {
            ...formData,
            returnLocation: location,
            returnLocationInfo: { address, lat, lng }
          };
          setFormData(updatedData);
          setTouched({ ...touched, returnLocation: true });

          // Clear error when location is selected
          if (errors.returnLocation) {
            setErrors((prev) => ({ ...prev, returnLocation: undefined }));
          }

          // Hide validation error message when user starts fixing
          if (showValidationError) {
            setShowValidationError(false);
          }

          // Call onChange callback
          if (onChange) {
            onChange(updatedData);
          }

          // true를 반환하여 모달을 닫음
          return true;
        }}
        title={t('checkout.selectReturnLocation')}
      />

      {/* Login Modal */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
      />

      {/* 만 21세 미만 예약 불가 안내 */}
      {isAgeRestrictionModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6 text-center">
            <div className="mb-4">
              <svg className="w-12 h-12 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              {t('checkout.ageRestrictionTitle')}
            </h3>
            <p className="text-sm text-gray-700">{t('checkout.ageRestriction')}</p>
            <p className="mt-2 text-xs text-gray-500">{t('checkout.ageRestrictionNote')}</p>
            <button
              type="button"
              onClick={() => setIsAgeRestrictionModalOpen(false)}
              className="mt-6 w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold transition-colors"
            >
              {t('common.confirm')}
            </button>
          </div>
        </div>
      )}
    </form>
  );
};

export default ReservationForm;
